import { assumptionSpecSchema, householdAssetSchema, householdDependentSchema, householdIncomeStreamSchema, householdLiabilitySchema, householdLifeEventSchema, householdPersonSchema, householdSpendingItemSchema, householdTrustSchema, marketShockSchema, plannedEventSchema, propertyDetailsSchema, householdGoalSchema, } from './schemas-household.js';
/** The set of displays T6 expects thousand separators on. */
export const GROUPED_DISPLAYS = Object.freeze(['currency', 'percentage']);
/** Shorthand so the table below stays readable at 138 entries. */
const f = (display, tier, extra) => Object.freeze({ display, tier, ...extra });
/**
 * The schemas in scope for field semantics: every contract an entity editor
 * binds to. Envelope, auth, billing and projection-result schemas are out of
 * scope — they are not adviser-entered fact-find fields.
 *
 * The completeness test walks THIS map, so adding a schema here immediately
 * demands its fields be classified.
 */
export const SEMANTIC_SCHEMAS = Object.freeze({
    householdPerson: householdPersonSchema,
    householdDependent: householdDependentSchema,
    householdAsset: householdAssetSchema,
    householdIncomeStream: householdIncomeStreamSchema,
    householdSpendingItem: householdSpendingItemSchema,
    householdLiability: householdLiabilitySchema,
    householdLifeEvent: householdLifeEventSchema,
    plannedEvent: plannedEventSchema,
    householdGoal: householdGoalSchema,
    marketShock: marketShockSchema,
    assumptionSpec: assumptionSpecSchema,
    propertyDetails: propertyDetailsSchema,
    householdTrust: householdTrustSchema,
});
/** Cap paths, named once so a typo shows up as one failure rather than many. */
const CAP = Object.freeze({
    isaSubscription: 'isa.annualSubscriptionLimit',
    lisa: 'isa.lisaAnnualLimit',
    jisa: 'isa.jisaAnnualLimit',
    pensionAnnual: 'pension.annualAllowance',
    giftExemption: 'inheritanceTax.annualGiftExemption',
    redundancy: 'incomeTax.redundancyExemption',
});
/**
 * Contributions are capped by the wrapper they go into: the ISA subscription
 * limit is shared across cash and stocks ISAs, a LISA and a JISA have their own
 * lower limits, and a pension is bounded by the annual allowance. Wrappers with
 * no statutory contribution cap are simply absent.
 */
const CONTRIBUTION_CAP_BY_WRAPPER = Object.freeze({
    cash_isa: CAP.isaSubscription,
    stocks_isa: CAP.isaSubscription,
    lisa: CAP.lisa,
    jisa: CAP.jisa,
    dc_pension: CAP.pensionAnnual,
});
export const FIELD_SEMANTICS = Object.freeze({
    householdPerson: Object.freeze({
        id: f('identifier', 'basic'),
        name: f('none', 'basic'),
        dateOfBirth: f('none', 'basic'),
        // THE round-1 breach that shipped anyway (§3.2). Age is a function of date
        // of birth and the projection's start year; asking for it lets the two
        // disagree, and the engine then ages the household off the wrong one.
        currentAge: f('age', 'basic', {
            derived: {
                from: ['dateOfBirth'],
                note: 'Age comes from date of birth and the plan start year. It must never be an input.',
            },
        }),
        retirementAge: f('age', 'basic'),
        statePensionAge: f('age', 'advanced', {
            derived: {
                from: ['dateOfBirth'],
                overrideField: 'statePensionAge',
                note: 'Read off the statutory timetable for the birth date; an adviser may override it for a client with a protected date.',
            },
        }),
        deathAge: f('age', 'advanced'),
        taxRegion: f('none', 'advanced'),
        maritalStatus: f('none', 'advanced'),
        // A list of the person's own student loans (plan + the April the loan
        // first fell due). Not an age, not an amount and not derived from
        // anything else on the person: the write-off year is anchored to the
        // first-due year alone, so a date of birth must never move it.
        studentLoans: f('none', 'basic'),
        // How the age is known when the note gave an age, not a birthday
        // (remediation 2026-09-17 N20). Recorded context; the engine reads
        // `currentAge`. A nested object, so no figure of its own.
        ageEvidence: f('none', 'advanced'),
    }),
    /**
     * B2.1 — a recorded-not-modelled arrangement. Every field is `none`: not
     * one of them is money, an age or a rate, and the whole point is that none
     * of them reaches a calculation.
     */
    householdTrust: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        kind: f('none', 'basic'),
        role: f('none', 'basic'),
        note: f('none', 'advanced'),
    }),
    householdDependent: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        name: f('none', 'basic'),
        // A dependant's age is derived wherever school fees and university need
        // it, from the stated date of birth — or, when the note gave only an age,
        // from `ageEvidence` via the disclosed modelling date
        // (`dependentModellingDateOfBirth`). Never a separate age input.
        dateOfBirth: f('none', 'basic'),
        ageEvidence: f('none', 'basic'),
    }),
    householdAsset: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        ownerId: f('none', 'basic'),
        wrapper: f('none', 'basic'),
        currentValue: f('currency', 'basic'),
        costBasis: f('currency', 'advanced'),
        contribution: f('currency', 'basic', {
            capped: {
                rule: CONTRIBUTION_CAP_BY_WRAPPER,
                dependsOn: 'wrapper',
                note: 'The limit depends on the wrapper, and the ISA limit is shared across every ISA the household subscribes to in the same year.',
            },
        }),
        contributionFrequency: f('none', 'basic'),
        growthRateBps: f('percentage', 'basic'),
        yieldBps: f('percentage', 'advanced'),
        feeDragBps: f('percentage', 'advanced'),
        contributionEscalation: f('none', 'advanced'),
        property: f('none', 'advanced'),
        neverDrawFrom: f('none', 'advanced'),
        contributionStartYear: f('year', 'advanced'),
        contributionEndYear: f('year', 'advanced'),
        employerContribution: f('currency', 'advanced', {
            capped: {
                rule: CAP.pensionAnnual,
                dependsOn: 'contribution',
                note: 'Employer and member contributions share one annual allowance, so the headroom shown has to net off what is already entered.',
            },
        }),
        employerContributionFrequency: f('none', 'advanced'),
        accessAge: f('age', 'advanced'),
        dcSubType: f('none', 'advanced'),
        pensionProductSubtype: f('none', 'advanced'),
        dependentId: f('identifier', 'advanced'),
        bprRateBps: f('percentage', 'advanced'),
        cgtChattelsExempt: f('none', 'advanced'),
        valueUnverified: f('none', 'advanced'),
        notes: f('none', 'advanced'),
    }),
    householdIncomeStream: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        ownerId: f('none', 'basic'),
        type: f('none', 'basic'),
        sourceAssetId: f('identifier', 'basic'),
        // Descriptive employment metadata from the note (N18); never read by
        // the projection.
        roleTitle: f('none', 'advanced'),
        workingPattern: f('none', 'advanced'),
        employmentBasis: f('none', 'advanced'),
        payComponent: f('none', 'advanced'),
        amount: f('currency', 'basic'),
        frequency: f('none', 'basic'),
        startYear: f('year', 'advanced'),
        endYear: f('year', 'advanced'),
        startAge: f('age', 'basic'),
        endAge: f('age', 'advanced'),
        endsAtRetirement: f('none', 'advanced'),
        growthRateBps: f('percentage', 'advanced'),
        taxable: f('none', 'advanced'),
        // B2.3: a fact about where the income came from. Not money, not a rate,
        // and it changes no figure — it decides whether the plan says what it has
        // not modelled about the trust's own tax.
        fromTrust: f('none', 'advanced'),
        dbSpouseFractionBps: f('percentage', 'advanced'),
        pensionSharingDebitBps: f('percentage', 'advanced'),
    }),
    householdSpendingItem: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        // FG-F3-04: whose spending this is — an owned row stops when its owner
        // dies, a joint row scales by the household's stated survivor factor.
        ownerId: f('none', 'advanced'),
        category: f('none', 'basic'),
        amount: f('currency', 'basic'),
        frequency: f('none', 'basic'),
        startYear: f('year', 'advanced'),
        endYear: f('year', 'advanced'),
        inflationLinked: f('none', 'advanced'),
        escalation: f('none', 'advanced'),
        // A2.7: the recurring half of the gift ledger. `amount` deliberately
        // carries NO `capped` entry the way the life event's does — a recurring
        // gift is measured against the exemption in each year it runs, not once,
        // so pinning the yearly cap to a single figure the adviser types would
        // misdescribe it.
        isGift: f('none', 'advanced'),
        giftRecipient: f('none', 'advanced'),
        exemptionClaimed: f('none', 'advanced'),
        normalExpenditureNote: f('none', 'advanced'),
        // NOT derived, and the first classification here said it was.
        //
        // It was read as the monthly/annual counterpart of `amount` — one figure
        // typed, the other shown. The contract says otherwise a few lines from the
        // field: this is the OTHER figure a fact-find captured, typically the
        // client's own estimate alongside a statement-derived one, with
        // `amountBasis` recording which of the two `amount` holds. A household's
        // estimate is routinely well below what their statements show, and which
        // basis the plan uses changes the answer.
        //
        // Worth stating plainly because the test built on this entry was demanding
        // the control be made read-only, and obeying it would have deleted the
        // adviser's ability to record the second figure at all — a real capability
        // removed to satisfy a mistake in this table.
        amountAlternative: f('currency', 'advanced'),
        amountBasis: f('none', 'advanced'),
    }),
    householdLiability: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        kind: f('none', 'basic'),
        // FG-F6-04: whose debt it is, and what happens to it on that person's
        // death — an explicit choice, never an automatic clearance either way.
        ownerId: f('none', 'advanced'),
        onDeath: f('none', 'advanced'),
        balance: f('currency', 'basic'),
        interestRateBps: f('percentage', 'basic'),
        // Computable from the outstanding figure, the rate and the term, so it is
        // offered as a prefilled suggestion rather than demanded.
        monthlyPayment: f('currency', 'advanced', {
            derived: {
                from: ['balance', 'interestRateBps', 'endYear', 'interestOnly'],
                overrideField: 'monthlyPayment',
                note: 'Amortised from the outstanding figure, the rate and the term. The adviser may override it to match a statement.',
            },
        }),
        interestOnly: f('none', 'advanced'),
        endYear: f('year', 'basic'),
        startYear: f('year', 'advanced'),
        annualOverpayment: f('currency', 'advanced'),
        rateChanges: f('none', 'advanced'),
    }),
    householdLifeEvent: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        year: f('year', 'basic'),
        amount: f('currency', 'basic', {
            capped: {
                rule: CAP.giftExemption,
                dependsOn: 'isGift',
                note: 'Only a gift is measured against the yearly exemption; anything above it stays in the estate for seven years.',
            },
        }),
        direction: f('none', 'basic'),
        wrapper: f('none', 'advanced'),
        isGift: f('none', 'advanced'),
        // A2.1 gift ledger. All three sit behind `isGift`, like `bprRateBps`.
        giftRecipient: f('none', 'advanced'),
        giftRelationship: f('none', 'advanced'),
        giftOccasion: f('none', 'advanced'),
        // A2.8: the adviser's stated position on the annual exemption, behind
        // `isGift` like the rest of the ledger.
        exemptionClaimed: f('none', 'advanced'),
        // A2.2: free text, required only alongside the normal-expenditure claim.
        normalExpenditureNote: f('none', 'advanced'),
        bprRateBps: f('percentage', 'advanced'),
    }),
    // Client goals (2026-09-03): the client's stated aim as recorded — every
    // outcome is derived per run and never a field.
    householdGoal: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        source: f('none', 'advanced'),
        kind: f('none', 'basic'),
        // amount at an age
        anchor: f('none', 'basic'),
        target: f('currency', 'basic'),
        terms: f('none', 'basic'),
        series: f('none', 'basic'),
        // fund if there is surplus (Junior ISA)
        dependentId: f('identifier', 'basic'),
        assetId: f('identifier', 'advanced'),
        personId: f('none', 'basic'),
        liabilityId: f('identifier', 'basic'),
        isGift: f('none', 'advanced'),
        oneOff: f('none', 'basic'),
        purpose: f('none', 'basic'),
        amount: f('currency', 'basic'),
        escalation: f('none', 'advanced'),
        startAnchor: f('none', 'advanced'),
        endAnchor: f('none', 'advanced'),
        // spending window
        basis: f('none', 'basic'),
        basisAssumed: f('none', 'advanced'),
        fromAnchor: f('none', 'basic'),
        toAnchor: f('none', 'basic'),
    }),
    plannedEvent: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        kind: f('none', 'basic'),
        dependentId: f('identifier', 'basic'),
        // school fees
        feePerTerm: f('currency', 'basic'),
        presetId: f('identifier', 'advanced'),
        termsPerYear: f('count', 'advanced'),
        startAge: f('age', 'basic'),
        startYear: f('year', 'advanced'),
        endAge: f('age', 'basic'),
        endYear: f('year', 'advanced'),
        escalation: f('none', 'advanced'),
        siblingDiscountBps: f('percentage', 'advanced'),
        // A2.4: who pays the fees, and whether the payments reach the seven-year
        // clock. Both record the adviser's stated position; neither is assessed.
        paidBy: f('none', 'advanced'),
        ihtTreatment: f('none', 'advanced'),
        exemptionClaimed: f('none', 'advanced'),
        normalExpenditureNote: f('none', 'advanced'),
        // university
        annualTuition: f('currency', 'basic'),
        annualMaintenance: f('currency', 'basic'),
        durationYears: f('count', 'basic'),
        parentalContributionBps: f('percentage', 'advanced'),
        // property purchase and sale
        year: f('year', 'basic'),
        price: f('currency', 'basic'),
        deposit: f('currency', 'basic'),
        ownerId: f('none', 'advanced'),
        isPrimaryResidence: f('none', 'advanced'),
        isBuyToLet: f('none', 'advanced'),
        rentalYieldBps: f('percentage', 'advanced'),
        growthRateBps: f('percentage', 'advanced'),
        isFirstTimeBuyer: f('none', 'advanced'),
        isAdditionalDwelling: f('none', 'advanced'),
        mortgageRateBps: f('percentage', 'basic'),
        mortgageMonthlyPayment: f('currency', 'advanced', {
            derived: {
                from: ['price', 'deposit', 'mortgageRateBps', 'mortgageEndYear', 'mortgageInterestOnly'],
                overrideField: 'mortgageMonthlyPayment',
                note: 'Amortised from the purchase figure less the deposit, over the term, at the rate given.',
            },
        }),
        mortgageInterestOnly: f('none', 'advanced'),
        mortgageEndYear: f('year', 'advanced'),
        propertyId: f('identifier', 'basic'),
        sellingCostsBps: f('percentage', 'advanced'),
        // business sale
        assetId: f('identifier', 'basic'),
        badrQualifying: f('none', 'advanced'),
        destination: f('none', 'advanced'),
        // career break
        personId: f('identifier', 'basic'),
        redundancyLumpSum: f('currency', 'basic', {
            capped: {
                rule: CAP.redundancy,
                note: 'The first slice of a redundancy payment is free of tax; the balance is taxed as earnings in the year it is paid.',
            },
        }),
        // care and recurring
        annualCost: f('currency', 'basic'),
        amount: f('currency', 'basic'),
        everyNYears: f('count', 'basic'),
        direction: f('none', 'basic'),
    }),
    marketShock: Object.freeze({
        id: f('identifier', 'basic'),
        label: f('none', 'basic'),
        year: f('year', 'basic'),
        dropBpsByWrapper: f('percentage', 'advanced'),
        // C1.1 anchored signed path: where it starts, and the per-year deltas.
        anchor: f('none', 'basic'),
        path: f('percentage', 'advanced'),
        // HS-2 historical replay: sourced mortgage rate paths and the dataset
        // provenance. Written by the replay, never typed, so both are advanced.
        liabilityRatePaths: f('percentage', 'advanced'),
        dataset: f('none', 'advanced'),
    }),
    assumptionSpec: Object.freeze({
        preset: f('none', 'basic'),
        // An id naming a published firm version — a reference, not an amount.
        setVersionId: f('identifier', 'basic'),
        portfolio: f('none', 'basic'),
        horizonYears: f('count', 'basic'),
        overrides: f('none', 'advanced'),
    }),
    propertyDetails: Object.freeze({
        isBuyToLet: f('none', 'basic'),
        isPrimaryResidence: f('none', 'advanced'),
        rentalYieldBps: f('percentage', 'basic'),
        linkedLiabilityId: f('identifier', 'advanced'),
    }),
});
/** Look up one field's semantics, or `undefined` if it is not registered. */
export function fieldSemantics(schema, field) {
    return FIELD_SEMANTICS[schema]?.[field];
}
/** Every `schema.field` pair marked derived — T5's discovery input. */
export function derivedFields() {
    const out = [];
    for (const [schema, fields] of Object.entries(FIELD_SEMANTICS)) {
        for (const [field, semantics] of Object.entries(fields)) {
            if (semantics.derived !== undefined) {
                out.push({ schema, field, derived: semantics.derived });
            }
        }
    }
    return out;
}
/** Every `schema.field` pair marked capped — T7's discovery input. */
export function cappedFields() {
    const out = [];
    for (const [schema, fields] of Object.entries(FIELD_SEMANTICS)) {
        for (const [field, semantics] of Object.entries(fields)) {
            if (semantics.capped !== undefined) {
                out.push({ schema, field, capped: semantics.capped });
            }
        }
    }
    return out;
}
/** Every cap path the registry references, flattened and de-duplicated. */
export function referencedCapPaths() {
    const paths = new Set();
    for (const { capped } of cappedFields()) {
        if (typeof capped.rule === 'string')
            paths.add(capped.rule);
        else
            for (const path of Object.values(capped.rule))
                paths.add(path);
    }
    return [...paths].sort();
}
/**
 * The field names of a schema, seen through the same unwrapping the tests use:
 * effects/optional/nullable/default wrappers are transparent, and a union
 * contributes the union of its members' fields.
 *
 * Exported because the completeness test, T5 and T6 all need exactly this walk,
 * and three copies of it would drift.
 */
export function schemaFieldNames(schema) {
    const inner = unwrap(schema);
    const def = inner._def;
    const typeName = def?.typeName;
    if (typeName === 'ZodObject') {
        return Object.keys(inner.shape);
    }
    if (typeName === 'ZodDiscriminatedUnion' || typeName === 'ZodUnion') {
        const names = new Set();
        for (const option of def?.options ?? []) {
            for (const name of schemaFieldNames(option))
                names.add(name);
        }
        return [...names];
    }
    return [];
}
/** Peel the wrappers zod puts around an object so `.shape` is reachable. */
function unwrap(schema) {
    let current = schema;
    for (let i = 0; i < 30; i++) {
        const def = current._def;
        switch (def?.typeName) {
            case 'ZodEffects':
                current = def.schema;
                break;
            case 'ZodOptional':
            case 'ZodNullable':
            case 'ZodDefault':
            case 'ZodReadonly':
            case 'ZodBranded':
            case 'ZodCatch':
                current = (def.innerType ?? def.type);
                break;
            case 'ZodPipeline':
                current = def.out;
                break;
            default:
                return current;
        }
        if (current === undefined)
            return schema;
    }
    return current;
}
//# sourceMappingURL=field-semantics.js.map